import { useEffect, useMemo, useState } from "react";
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, CircleOff, Loader2, Plus, RefreshCw, TestTube2, Trash2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY_FORM = { key: "", name: "", module: "Custom", path: "/sap/opu/odata/sap/", enabled: true };

const statusStyle = {
  healthy: "border-emerald-200 bg-emerald-50 text-emerald-700",
  disabled: "border-zinc-200 bg-zinc-50 text-zinc-600",
  unauthorized: "border-amber-200 bg-amber-50 text-amber-700",
  timeout: "border-red-200 bg-red-50 text-red-700",
  unreachable: "border-red-200 bg-red-50 text-red-700",
  not_found: "border-red-200 bg-red-50 text-red-700",
  failed: "border-red-200 bg-red-50 text-red-700",
};

const EndpointStatus = ({ endpoint }) => {
  const status = endpoint.enabled === false ? "disabled" : endpoint.status || "not_tested";
  const Icon = status === "healthy" ? CheckCircle2 : status === "disabled" ? CircleOff : AlertCircle;
  return (
    <Badge variant="outline" className={`gap-1 capitalize ${statusStyle[status] || "border-slate-200 bg-slate-50 text-slate-600"}`}>
      <Icon className="h-3 w-3" />{status.replaceAll("_", " ")}
    </Badge>
  );
};

const SAPEndpointManager = ({ connection, onStats }) => {
  const [expanded, setExpanded] = useState(false);
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(null);
  const [filter, setFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const publishStats = (items) => {
    onStats?.(connection.id, {
      total: items.length,
      endpointKeys: items.map((item) => item.key).filter(Boolean),
      healthy: items.filter((item) => item.status === "healthy").length,
      failed: items.filter((item) => item.status && !["healthy", "disabled"].includes(item.status)).length,
    });
  };

  const fetchEndpoints = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await axios.get(`${API}/sap-connections/${connection.id}/endpoints`);
      setEndpoints(response.data.endpoints || []);
      publishStats(response.data.endpoints || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to load endpoints for ${connection.name}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEndpoints(); }, [connection.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = useMemo(() => ({
    all: endpoints.length,
    healthy: endpoints.filter((item) => item.status === "healthy").length,
    failed: endpoints.filter((item) => item.status && !["healthy", "disabled"].includes(item.status)).length,
  }), [endpoints]);

  const visible = endpoints.filter((item) => {
    if (filter === "healthy") return item.status === "healthy";
    if (filter === "failed") return item.status && !["healthy", "disabled"].includes(item.status);
    return true;
  });

  const testOne = async (endpoint) => {
    if (!connection.is_active) {
      toast.error("Activate this SAP connection before testing endpoints");
      return;
    }
    setTesting(endpoint.id);
    try {
      const response = await axios.post(`${API}/sap-connections/${connection.id}/endpoints/${encodeURIComponent(endpoint.id)}/test`);
      const next = endpoints.map((item) => item.id === endpoint.id ? response.data : item);
      setEndpoints(next);
      publishStats(next);
      toast[response.data.status === "healthy" ? "success" : "error"](`${endpoint.name}: ${response.data.message}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Endpoint test failed");
    } finally {
      setTesting(null);
    }
  };

  const testAll = async () => {
    if (!connection.is_active) {
      toast.error("Activate this SAP connection before testing endpoints");
      return;
    }
    setTesting("all");
    try {
      const response = await axios.post(`${API}/sap-connections/${connection.id}/endpoints/test-all`);
      const next = response.data.endpoints || [];
      setEndpoints(next);
      publishStats(next);
      toast.success(`Tested ${next.length} SAP endpoints`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Endpoint tests failed");
    } finally {
      setTesting(null);
    }
  };

  const createEndpoint = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/sap-connections/${connection.id}/endpoints`, form);
      toast.success("SAP endpoint added");
      setDialogOpen(false);
      setForm(EMPTY_FORM);
      await fetchEndpoints(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add endpoint");
    } finally {
      setSaving(false);
    }
  };

  const removeEndpoint = async (endpoint) => {
    if (!window.confirm(`Remove endpoint "${endpoint.name}"?`)) return;
    try {
      await axios.delete(`${API}/sap-connections/${connection.id}/endpoints/${encodeURIComponent(endpoint.id)}`);
      toast.success("Endpoint removed");
      await fetchEndpoints(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove endpoint");
    }
  };

  return (
    <div className="mt-3 border-t border-border pt-3" data-testid={`endpoint-manager-${connection.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 text-left">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          <span className="font-semibold">SAP Endpoints</span>
          <Badge variant="secondary">{endpoints.length}</Badge>
        </button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={testAll} disabled={!connection.is_active || testing === "all"} title={!connection.is_active ? "Activate this SAP connection before testing its endpoints" : "Test all SAP endpoints"}>
            {testing === "all" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <TestTube2 className="mr-1.5 h-4 w-4" />}Test All
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setDialogOpen(true)} data-testid={`add-endpoint-${connection.id}`}>
            <Plus className="mr-1.5 h-4 w-4" />Add Endpoint
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 space-y-2.5">
          <div className="rounded-md border border-blue-200 bg-blue-50/70 px-3 py-1.5 text-xs text-blue-800">
            Standard endpoints are generated from the SAP base URL. Add a custom endpoint or override a standard path when a customer system uses a different service.
          </div>
          <div className="flex gap-2">
            {["all", "healthy", "failed"].map((item) => (
              <Button key={item} size="sm" variant={filter === item ? "default" : "outline"} onClick={() => setFilter(item)} className="h-8 px-3 text-xs capitalize">
                {item} {counts[item]}
              </Button>
            ))}
            <Button variant="ghost" size="sm" onClick={() => fetchEndpoints()} disabled={loading} className="ml-auto">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
          <div className="overflow-hidden rounded-md border">
            <Table className="[&_th]:h-8 [&_td]:py-1.5">
              <TableHeader><TableRow><TableHead>Module</TableHead><TableHead>Endpoint</TableHead><TableHead className="max-w-[360px]">Service Path</TableHead><TableHead>Status</TableHead><TableHead>Last Tested</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {visible.map((endpoint) => (
                  <TableRow key={endpoint.id}>
                    <TableCell className="text-xs font-medium">{endpoint.module}</TableCell>
                    <TableCell><div className="text-sm font-medium">{endpoint.name}</div><div className="text-[11px] text-muted-foreground">{endpoint.key} · {endpoint.source}</div></TableCell>
                    <TableCell className="max-w-[360px] truncate font-mono text-[11px]" title={endpoint.path}>{endpoint.path}</TableCell>
                    <TableCell><EndpointStatus endpoint={endpoint} /></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{endpoint.last_tested_at ? new Date(endpoint.last_tested_at).toLocaleString() : "Never"}</TableCell>
                    <TableCell><div className="flex justify-end gap-1"><Button variant="ghost" size="sm" onClick={() => testOne(endpoint)} disabled={!connection.is_active || Boolean(testing) || endpoint.enabled === false} title={!connection.is_active ? "Activate this SAP connection before testing this endpoint" : "Test SAP endpoint"}>{testing === endpoint.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <TestTube2 className="h-4 w-4" />}<span className="sr-only">Test</span></Button>{endpoint.source !== "standard" && <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeEndpoint(endpoint)}><Trash2 className="h-4 w-4" /><span className="sr-only">Remove</span></Button>}</div></TableCell>
                  </TableRow>
                ))}
                {!loading && visible.length === 0 && <TableRow><TableCell colSpan={6} className="h-16 text-center text-muted-foreground">No endpoints match this filter.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader><DialogTitle>Add SAP Endpoint</DialogTitle><DialogDescription>Add a safe relative OData path. Credentials and host are inherited from {connection.name}.</DialogDescription></DialogHeader>
          <form onSubmit={createEndpoint} className="space-y-4">
            <div className="grid grid-cols-2 gap-4"><div className="space-y-2"><Label htmlFor="endpoint-name">Display Name</Label><Input id="endpoint-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Custom Stock Service" required /></div><div className="space-y-2"><Label htmlFor="endpoint-key">Logical Key</Label><Input id="endpoint-key" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} placeholder="custom_stock" required /></div></div>
            <div className="space-y-2"><Label>Module</Label><Select value={form.module} onValueChange={(module) => setForm({ ...form, module })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Custom", "Finance", "Sales", "Master Data", "Supply Chain", "Production", "Purchasing"].map((module) => <SelectItem key={module} value={module}>{module}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-2"><Label htmlFor="endpoint-path">Relative OData Path</Label><Input id="endpoint-path" className="font-mono text-xs" value={form.path} onChange={(e) => setForm({ ...form, path: e.target.value })} placeholder="/sap/opu/odata/sap/Z_CUSTOM_SRV/EntitySet" required /><p className="text-xs text-muted-foreground">Must start with /sap/opu/odata/sap/. Full URLs and query strings are not accepted.</p></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit" disabled={saving}>{saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Add Endpoint</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SAPEndpointManager;
